import { type ReactElement, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { WorkflowTimeline } from "@compositions/index";
import type { TimelineFilterControls, TimelineFilterWorkItem } from "@compositions/index";
import { useWorkflow } from "../hooks";
import {
  applyTimelineFilters,
  parseHiddenActivityTypes,
  presentActivityTypes,
  serializeHiddenActivityTypes,
  useWorkflowTimeline,
  workItemFilterLabel,
} from "../timeline";

export interface TimelineBodyProps {
  readonly id: string;
  readonly entityType: string;
  readonly entityId: string;
}

// `item` is the same axis the board owns: a task selected on the board
// carries across as a "show this item's history" filter. `hide` is
// timeline-only (csv of suppressed activity types).
const ITEM_PARAM = "item";
const HIDE_PARAM = "hide";

/**
 * Timeline view body. Hydrates the feed and owns the filter state (URL-
 * driven). Filtering is a pure transform over the loaded feed; see
 * `applyTimelineFilters` for why the ledger's by-entity read contract
 * makes the presentation layer the right seam. The scene owns the header
 * and the realtime subscription.
 */
export function TimelineBody({ id, entityType, entityId }: TimelineBodyProps): ReactElement {
  const { data: workflow } = useWorkflow(id, entityType, entityId);
  const timelineQuery = useWorkflowTimeline({ workflowId: id, entityType, entityId });

  const events = useMemo(() => timelineQuery.data?.events ?? [], [timelineQuery.data?.events]);
  const truncated = timelineQuery.data?.truncated ?? false;
  const partial = timelineQuery.data?.partial ?? false;

  const { refetch } = timelineQuery;
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const [searchParams, setSearchParams] = useSearchParams();

  const selectedWorkItemId = useMemo<string | null>(() => {
    const value = searchParams.get(ITEM_PARAM) ?? "";
    return value.length > 0 ? value : null;
  }, [searchParams]);

  const hiddenActivityTypes = useMemo(
    () => parseHiddenActivityTypes(searchParams.get(HIDE_PARAM)),
    [searchParams],
  );

  const filteredEvents = useMemo(
    () => applyTimelineFilters(events, { workItemId: selectedWorkItemId, hiddenActivityTypes }),
    [events, selectedWorkItemId, hiddenActivityTypes],
  );

  // Rail vocabularies derive from the unfiltered feed so they stay stable
  // while a filter is active.
  const activityTypes = useMemo(() => presentActivityTypes(events), [events]);

  const filterWorkItems = useMemo<readonly TimelineFilterWorkItem[]>(() => {
    if (workflow === undefined) return [];
    return workflow.workItems
      .filter((item) => item.type === "task" || item.type === "note")
      .map((item) => ({
        id: item.id,
        type: item.type,
        label: workItemFilterLabel(item.type, item.body),
      }));
  }, [workflow]);

  const handleSelectWorkItem = useCallback(
    (next: string | null) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (next === null) params.delete(ITEM_PARAM);
        else params.set(ITEM_PARAM, next);
        return params;
      });
    },
    [setSearchParams],
  );

  const writeHidden = useCallback(
    (next: ReadonlySet<string>) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        const serialized = serializeHiddenActivityTypes(next);
        if (serialized.length === 0) params.delete(HIDE_PARAM);
        else params.set(HIDE_PARAM, serialized);
        return params;
      });
    },
    [setSearchParams],
  );

  const handleToggleActivityType = useCallback(
    (type: string) => {
      const next = new Set(hiddenActivityTypes);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      writeHidden(next);
    },
    [hiddenActivityTypes, writeHidden],
  );

  const handleResetActivityTypes = useCallback(() => writeHidden(new Set()), [writeHidden]);

  const handleClearAll = useCallback(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete(ITEM_PARAM);
      params.delete(HIDE_PARAM);
      return params;
    });
  }, [setSearchParams]);

  // Offer the rail only once there is activity to filter.
  const filters: TimelineFilterControls | undefined =
    events.length > 0
      ? {
          workItems: filterWorkItems,
          selectedWorkItemId,
          onSelectWorkItem: handleSelectWorkItem,
          activityTypes,
          hiddenActivityTypes,
          onToggleActivityType: handleToggleActivityType,
          onResetActivityTypes: handleResetActivityTypes,
          onClearAll: handleClearAll,
        }
      : undefined;

  return (
    <WorkflowTimeline
      embedded
      events={filteredEvents}
      // `isPending` (no data yet), not `isLoading` (pending AND fetching):
      // the first frame after the body mounts can precede the fetch, and it
      // must paint the skeleton, never the empty state.
      isLoading={timelineQuery.isPending}
      isStale={timelineQuery.isPlaceholderData}
      error={timelineQuery.error as Error | null}
      truncated={truncated}
      partial={partial}
      onRetry={handleRetry}
      {...(filters !== undefined ? { filters } : {})}
    />
  );
}
